import { Gender, Position } from '@statsbomb/parachute-types';
import { Getter } from 'jotai';
import { cloneDeep, set } from 'lodash';
import { playerPositionsOptionsAtom, playerPositionsSettledAtom } from '@/atoms/queries/player/playerPositions';
import {
  canSeePhysicalRadarAtom,
  dashboardTemplatesAtom,
  dashboardWidgetsAtom,
  genderAtom,
} from '@/atoms/queries/userConfigs';
import {
  PHYSICAL_RADAR_WIDGET_CONFIG_ID,
  PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN,
  PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN,
  POSITION_TEMPLATE_MAP_MEN,
  POSITION_TEMPLATE_MAP_WOMEN,
  TEAM_RADAR_TEMPLATE_CONFIG_IDS_MEN,
  TEAM_RADAR_TEMPLATE_CONFIG_IDS_WOMEN,
} from '@/consts/radar';
import { DEFAULT_PLAYER_DASHBOARD_ID, DEFAULT_TEAM_DASHBOARD_ID } from '@/consts/userConfigs';
import { UserConfigWidgetDefinition } from '@/types/userConfigs';
import { isGkPosition } from '@/utils/radar';

const templateIdPath = 'definition.displaySettings.radarTemplateId';

const buildPhysicalRadarWidget = (
  radarWidget: UserConfigWidgetDefinition,
  physicalTemplateId: string,
): UserConfigWidgetDefinition => {
  const physicalWidget = cloneDeep(radarWidget);
  physicalWidget.configId = PHYSICAL_RADAR_WIDGET_CONFIG_ID;
  physicalWidget.definition.titleSettings = { title: 'physicalRadar', translate: true };
  return set(physicalWidget, templateIdPath, physicalTemplateId);
};

// get a list of all the widget configs that are referenced in a dashboard template of a given id
export const getWidgetsByDashboardId = async (get: Getter, id: string) => {
  const [dashboardTemplates, dashboardWidgets, genderValue] = await Promise.all([
    get(dashboardTemplatesAtom),
    get(dashboardWidgetsAtom),
    get(genderAtom),
  ]);

  const dashboardTemplate = dashboardTemplates.find(template => template.configId === id);
  if (!dashboardTemplate) return [];

  const dashboardWidgetIds = dashboardTemplate.definition.widgetIds;

  // Doing this way to maintain the order of widgets as per the dashboard template
  const entityDashboardWidgets = dashboardWidgetIds
    .map(widgetId => dashboardWidgets.find(widget => widget.configId === widgetId))
    // As a precaution, filter out any widgets we were not able to find
    .filter((widget): widget is UserConfigWidgetDefinition => Boolean(widget));

  if (!entityDashboardWidgets.length) return [];
  const gender = genderValue || Gender.M;

  // statsbomb shared radar widgets are unique as they don't have a radar template id set in their config. The reason for this is
  // that the radar template is dynamic based on the selected dashboard (player/team) and the user's selected competition gender.
  // This determines which radar template to use based on the selected dashboard id and user's selected competition gender
  const firstWidget = cloneDeep(entityDashboardWidgets[0]);
  const secondWidget = cloneDeep(entityDashboardWidgets[1]);

  if (id === DEFAULT_PLAYER_DASHBOARD_ID) {
    const positionMap = gender === Gender.M ? POSITION_TEMPLATE_MAP_MEN : POSITION_TEMPLATE_MAP_WOMEN;
    const [positions, canSeePhysicalRadar, positionsSettled] = await Promise.all([
      get(playerPositionsOptionsAtom),
      get(canSeePhysicalRadarAtom),
      get(playerPositionsSettledAtom),
    ]);
    const raw = positions[0]?.value;
    const primaryPosition = Object.values(Position).includes(raw as Position) ? (raw as Position) : undefined;
    const radarTemplateId = primaryPosition ? positionMap[primaryPosition] : positionMap.cf;
    const newFirstWidget = set(firstWidget, templateIdPath, radarTemplateId);

    // player dashboard only has one radar widget and it's the first one, so we only need to update the first widget
    const playerDashboardWidgets = entityDashboardWidgets.with(0, newFirstWidget);

    const showPhysicalRadar = canSeePhysicalRadar && positionsSettled && !isGkPosition(primaryPosition);
    const radarWidgetIndex = playerDashboardWidgets.findIndex(widget => widget.definition.vis === 'radar');
    if (!showPhysicalRadar || radarWidgetIndex === -1) return playerDashboardWidgets;

    const physicalTemplateId =
      gender === Gender.M
        ? PLAYER_RADAR_TEMPLATE_CONFIG_IDS_MEN.PHYSICAL
        : PLAYER_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.PHYSICAL;
    const physicalWidget = buildPhysicalRadarWidget(playerDashboardWidgets[radarWidgetIndex], physicalTemplateId);
    return playerDashboardWidgets.toSpliced(radarWidgetIndex + 1, 0, physicalWidget);
  }

  if (id === DEFAULT_TEAM_DASHBOARD_ID) {
    const teamRadarTemplateIds =
      gender === Gender.M ? TEAM_RADAR_TEMPLATE_CONFIG_IDS_MEN : TEAM_RADAR_TEMPLATE_CONFIG_IDS_WOMEN;
    const newFirstWidget = set(firstWidget, templateIdPath, teamRadarTemplateIds.ATTACKING);
    const newSecondWidget = set(secondWidget, templateIdPath, teamRadarTemplateIds.DEFENDING);

    // team dashboard has two radar widgets, so we need to update both the first and second widgets
    return entityDashboardWidgets.with(0, newFirstWidget).with(1, newSecondWidget);
  }

  return entityDashboardWidgets;
};
